import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import Icon from '../AppIcon';
import { notificationService } from '../../services/notificationService';
import NotificationPanel from '../notifications/NotificationPanel';
import NotificationToast from '../notifications/NotificationToast';


const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut, isAuthenticated, isAdmin } = useAuth();
  const { checkAccess } = useSubscription();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [toastNotification, setToastNotification] = useState(null);
  const alertPanelRef = useRef(null);
  const userMenuRef = useRef(null);
  const notificationPanelRef = useRef(null);

  const allNavigationItems = [
    {
      label: 'Wallet',
      path: '/user-wallet-dashboard',
      icon: 'Wallet',
      description: 'Customer wallet dashboard',
      requiresAuth: true,
      requiresPro: false
    },
    {
      label: 'Swap',
      path: '/swap-engine-interface',
      icon: 'RefreshCw',
      description: 'Asset swap engine',
      requiresAuth: true,
      requiresPro: false
    },
    {
      label: 'Payments',
      path: '/payment-spending-hub',
      icon: 'CreditCard',
      description: 'Transaction & spending management',
      requiresAuth: true,
      requiresPro: false
    },
    {
      label: 'Merchant',
      path: '/merchant-payment-widget',
      icon: 'Store',
      description: 'Embedded payment widget',
      requiresAuth: true,
      requiresPro: false
    },
    {
      label: 'Digital Card',
      path: '/digital-visa-card-management',
      icon: 'CreditCard',
      description: 'Virtual Visa card management',
      requiresAuth: true,
      requiresPro: true
    }
  ];

  // User dropdown menu items
  const userMenuItems = [
    {
      label: '2FA Setup',
      path: '/two-factor-authentication-setup',
      icon: 'Shield',
      description: 'Configure two-factor authentication'
    },
    {
      label: '2FA Management',
      path: '/two-factor-authentication-management',
      icon: 'ShieldCheck',
      description: 'Manage security settings'
    },
    {
      label: 'Email Preferences',
      path: '/email-notification-preferences',
      icon: 'Mail',
      description: 'Configure email notifications'
    },
    {
      label: 'Billing',
      path: '/billing-invoices',
      icon: 'Receipt',
      description: 'Invoices and payment history'
    },
    {
      label: 'Legal Terms',
      path: '/legal-disclaimer-terms-agreement',
      icon: 'FileText',
      description: 'Legal disclaimer & terms agreement'
    }
  ];

  // Admin-only navigation items
  const adminNavigationItems = [
    {
      label: 'Admin Center',
      path: '/main-admin-control-center',
      icon: 'Shield',
      description: 'Main admin control center',
      requiresAuth: true,
      requiresAdmin: true
    },
    {
      label: 'Sessions',
      path: '/admin-session-management',
      icon: 'Users',
      description: 'Admin session management',
      requiresAuth: true,
      requiresAdmin: true
    },
    {
      label: 'Operations',
      path: '/real-time-operations',
      icon: 'Activity',
      description: 'Real-time system monitoring',
      requiresAuth: true,
      requiresAdmin: true
    },
    {
      label: 'Users',
      path: '/user-analytics-hub',
      icon: 'Users',
      description: 'User behavior analytics',
      requiresAuth: true,
      requiresAdmin: true
    },
    {
      label: 'Finance',
      path: '/financial-performance',
      icon: 'DollarSign',
      description: 'Revenue and cost analytics',
      requiresAuth: true,
      requiresAdmin: true
    },
    {
      label: 'Executive',
      path: '/executive-overview',
      icon: 'BarChart3',
      description: 'Strategic overview',
      requiresAuth: true,
      requiresAdmin: true
    },
    {
      label: 'Payouts',
      path: '/pro-payout-management',
      icon: 'Banknote',
      description: 'Pro payout & settlement tracking',
      requiresAuth: true,
      requiresAdmin: true
    }
  ];

  // Filter navigation items based on authentication and subscription
  const navigationItems = allNavigationItems?.filter(item => {
    // If item requires authentication and user is not authenticated, hide it
    if (item?.requiresAuth && !isAuthenticated) {
      return false;
    }
    
    // If item requires Pro and user doesn't have Pro access, hide it
    if (item?.requiresPro && !checkAccess('pro')) {
      return false;
    }
    
    return true;
  });

  // Filter admin items - only show if user is admin
  const visibleAdminItems = isAdmin && isAuthenticated ? adminNavigationItems : [];

  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user?.id) {
      loadUnreadCount();
      
      const unsubscribe = notificationService?.subscribeToNotifications(
        user?.id,
        (newNotification) => {
          setUnreadCount(prev => prev + 1);
          setToastNotification(newNotification);
        }
      );

      return () => {
        unsubscribe();
      };
    }
  }, [user?.id]);

  const loadUnreadCount = async () => {
    try {
      const count = await notificationService?.getUnreadCount(user?.id);
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alertPanelRef?.current && !alertPanelRef?.current?.contains(event?.target)) {
        setIsAlertPanelOpen(false);
      }
      if (notificationPanelRef?.current && !notificationPanelRef?.current?.contains(event?.target)) {
        setIsNotificationPanelOpen(false);
      }
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isAlertPanelOpen || isNotificationPanelOpen || isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAlertPanelOpen, isNotificationPanelOpen, isUserMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleAlertClick = (alertId) => {
    setAlerts(alerts?.map(alert => 
      alert?.id === alertId ? { ...alert, unread: false } : alert
    ));
  };

  const handleSignOut = async () => {
    await signOut();
    setIsUserMenuOpen(false);
    navigate('/signin');
  };

  const handleNotificationClick = () => {
    setIsNotificationPanelOpen(!isNotificationPanelOpen);
    if (!isNotificationPanelOpen) {
      loadUnreadCount();
    }
  };

  const getTierBadge = () => {
    const tier = userProfile?.tier;
    if (tier === 'pro') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary rounded text-xs font-semibold">
          <Icon name="Crown" size={12} />
          Pro
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs font-semibold">
        Free
      </span>
    );
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'text-error';
      case 'warning':
        return 'text-warning';
      case 'success':
        return 'text-success';
      default:
        return 'text-foreground';
    }
  };

  return (
    <>
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="mobile-menu-trigger"
        aria-label="Toggle mobile menu"
      >
        <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>
      <nav className="nav-container">
        <div className="nav-content">
          <div className="nav-brand">
            <div className="nav-logo">
              <img src="/assets/images/22458-1768718790784.png" alt="BOLTPay Logo" className="w-6 h-6 object-contain" />
            </div>
            <span className="nav-title">BOLTPay</span>
          </div>

          {/* Admin Navigation Row - Below Logo */}
          {visibleAdminItems?.length > 0 && (
            <div className="hidden lg:flex items-center gap-1 px-4 py-1 bg-primary/10 rounded-lg border border-primary/20">
              <Icon name="Shield" size={14} className="text-primary" />
              {visibleAdminItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-smooth ${
                    location?.pathname === item?.path 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                  aria-current={location?.pathname === item?.path ? 'page' : undefined}
                >
                  {item?.label}
                </button>
              ))}
            </div>
          )}

          <div className="hidden lg:flex nav-menu">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`nav-item ${location?.pathname === item?.path ? 'active' : ''}`}
                aria-current={location?.pathname === item?.path ? 'page' : undefined}
              >
                {item?.label}
              </button>
            ))}
          </div>

          <div className="nav-controls">
            {/* Notification Bell */}
            <div className="relative" ref={notificationPanelRef}>
              <button
                onClick={handleNotificationClick}
                className="relative p-2 text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="Bell" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-error text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <NotificationPanel 
                isOpen={isNotificationPanelOpen} 
                onClose={() => setIsNotificationPanelOpen(false)}
              />
            </div>

            {/* Alert Bell */}
            <div className="relative" ref={alertPanelRef}>
              <button
                onClick={() => setIsAlertPanelOpen(!isAlertPanelOpen)}
                className="relative p-2 text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="AlertCircle" size={20} />
                {alerts?.filter(alert => alert?.unread)?.length > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-error text-white text-xs rounded-full flex items-center justify-center">
                    {alerts?.filter(alert => alert?.unread)?.length}
                  </span>
                )}
              </button>

              {isAlertPanelOpen && (
                <div className="alert-panel">
                  <div className="alert-header">
                    <h3 className="text-base font-semibold text-foreground">Notifications</h3>
                    <button
                      onClick={() => setIsAlertPanelOpen(false)}
                      className="p-1 rounded hover:bg-muted/50 transition-smooth"
                      aria-label="Close alerts"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                  <div className="alert-list scrollbar-thin">
                    {alerts?.map((alert) => (
                      <div
                        key={alert?.id}
                        onClick={() => handleAlertClick(alert?.id)}
                        className={`alert-item ${alert?.unread ? 'unread' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon 
                            name={alert?.severity === 'error' ? 'AlertCircle' : alert?.severity === 'warning' ? 'AlertTriangle' : 'CheckCircle'} 
                            size={20} 
                            className={getSeverityColor(alert?.severity)}
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-foreground mb-1">{alert?.title}</h4>
                            <p className="text-sm text-muted-foreground mb-2">{alert?.message}</p>
                            <span className="caption text-muted-foreground">{alert?.timestamp}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {isAuthenticated && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/50 transition-smooth"
                  aria-label="User menu"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-foreground">{userProfile?.full_name || 'User'}</p>
                    {getTierBadge()}
                  </div>
                  <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg py-2 z-50">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold text-foreground">{userProfile?.full_name || 'User'}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      <div className="mt-2">{getTierBadge()}</div>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-smooth flex items-center gap-2"
                    >
                      <Icon name="Settings" size={16} />
                      Profile Settings
                    </button>
                    <button
                      onClick={() => {
                        navigate('/user-wallet-dashboard');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-smooth flex items-center gap-2"
                    >
                      <Icon name="Wallet" size={16} />
                      My Wallet
                    </button>
                    <div className="border-t border-border my-2"></div>
                    {userMenuItems?.map((item) => (
                      <button
                        key={item?.path}
                        onClick={() => {
                          navigate(item?.path);
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-smooth flex items-center gap-2"
                      >
                        <Icon name={item?.icon} size={16} />
                        {item?.label}
                      </button>
                    ))}
                    <div className="border-t border-border my-2"></div>
                    <button
                      onClick={handleSignOut}
                      className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 transition-smooth flex items-center gap-2"
                    >
                      <Icon name="LogOut" size={16} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}

            <ConnectionStatus />
          </div>
        </div>
      </nav>
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu-content">
            <div className="mobile-menu-header">
              <div className="nav-brand">
                <div className="nav-logo">
                  <img src="/assets/images/22458-1768718790784.png" alt="BOLTPay Logo" className="w-6 h-6 object-contain" />
                </div>
                <span className="nav-title">BOLTPay</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-muted/50 transition-smooth"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <nav className="mobile-menu-nav">
              {visibleAdminItems?.length > 0 && (
                <div className="px-4 py-2 bg-primary/10 border-l-4 border-primary mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Shield" size={16} className="text-primary" />
                    <span className="text-sm font-semibold text-primary">Admin Features</span>
                  </div>
                  {visibleAdminItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className={`mobile-nav-item ${location?.pathname === item?.path ? 'active' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon name={item?.icon} size={20} />
                        <div className="text-left">
                          <div className="font-semibold">{item?.label}</div>
                          <div className="caption text-muted-foreground">{item?.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`mobile-nav-item ${location?.pathname === item?.path ? 'active' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon name={item?.icon} size={20} />
                    <div className="text-left">
                      <div className="font-semibold">{item?.label}</div>
                      <div className="caption text-muted-foreground">{item?.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
      {toastNotification && (
        <NotificationToast
          notification={toastNotification}
          onClose={() => setToastNotification(null)}
        />
      )}
    </>
  );
};

const ConnectionStatus = () => {
  const [status, setStatus] = useState('connected');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusText = () => {
    switch (status) {
      case 'connected':
        return 'Live';
      case 'connecting':
        return 'Connecting';
      case 'disconnected':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div className="connection-status">
      <div className={`connection-dot ${status}`} />
      <div className="flex flex-col">
        <span className="connection-text text-foreground">{getStatusText()}</span>
        <span className="connection-text text-muted-foreground text-[10px]">
          {formatTimestamp(lastUpdate)}
        </span>
      </div>
    </div>
  );
};

export default Header;